import {
  BarChart3,
  Calculator,
  CalendarDays,
  CheckSquare,
  ClipboardList,
  DoorOpen,
  FileText,
  HandHelping,
  Heart,
  Home,
  Layers,
  Megaphone,
  Package,
  Send,
  Settings,
  ShieldCheck,
  Truck,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";

import type { ModulesEnabled } from "@/features/manifest/model/types";
import type { PendingCounts } from "@/features/notifications/api/pendingCountsApi";

import type { VerticalVocab } from "@/shared/lib/verticals";

export type NavGroupId =
  | "services"
  | "scheduling"
  | "finance"
  | "management"
  | "platform";

export interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  vocabKey?: keyof VerticalVocab["nav"];
  requiresModule?: keyof ModulesEnabled | "scheduling";
  requiresControl?: boolean;
  requiresRole?: string[];
  permissionMatrixModule?: string;
  /** Show if the user has view, create, edit, or delete on permissionMatrixModule. */
  visibleWithAnyCrud?: boolean;
  badgeKey?: keyof Omit<PendingCounts, "total">;
  /** Parent row that toggles nested accounting sections; does not navigate. */
  nestedAccounting?: boolean;
  /** Parent row that toggles Grants → Grant pipeline / Grant management; does not navigate. */
  nestedGrants?: boolean;
  /** Settings hub: visible when any settings tab is available (not only settings module). */
  settingsHub?: boolean;
}

export interface NavGroup {
  id: NavGroupId;
  label: string;
  items: NavItem[];
}

export const UNGROUPED_ITEMS: NavItem[] = [{ to: "/", label: "Dashboard", icon: Home }];

export const NAV_GROUPS: NavGroup[] = [
  {
    id: "services",
    label: "Services",
    items: [
      {
        to: "/tasks",
        label: "Tasks",
        icon: CheckSquare,
        badgeKey: "task_approvals",
        vocabKey: "tasks",
        requiresModule: "tasks",
        requiresRole: ["OWNER", "ADMIN", "STAFF", "MENTOR", "PROGRAM_DIRECTOR", "VOLUNTEER"],
      },
      {
        to: "/check-in",
        label: "Check-In",
        icon: ClipboardList,
        vocabKey: "checkIn",
        requiresModule: "checkin",
        requiresRole: ["OWNER", "ADMIN", "STAFF", "MENTOR", "PROGRAM_DIRECTOR", "VOLUNTEER"],
      },
      {
        to: "/community",
        label: "Community",
        icon: Users,
        vocabKey: "community",
        requiresModule: "people",
        permissionMatrixModule: "members",
        requiresRole: ["OWNER", "ADMIN", "STAFF", "MENTOR", "PROGRAM_DIRECTOR"],
      },
      {
        to: "/assistance-programs",
        label: "Programs",
        icon: HandHelping,
        requiresModule: "programs",
        permissionMatrixModule: "programs",
        requiresRole: [
          "OWNER",
          "ADMIN",
          "ACCOUNTANT",
          "STAFF",
          "MENTOR",
          "PROGRAM_DIRECTOR",
          "VOLUNTEER",
        ],
      },
      {
        to: "/inventory",
        label: "Inventory",
        icon: Package,
        vocabKey: "inventory",
        requiresModule: "inventory",
        requiresRole: ["OWNER", "ADMIN", "STAFF", "MENTOR", "PROGRAM_DIRECTOR", "VOLUNTEER"],
      },
    ],
  },
  {
    id: "scheduling",
    label: "Scheduling",
    items: [
      {
        to: "/scheduling",
        label: "Scheduling",
        icon: CalendarDays,
        vocabKey: "scheduling",
        requiresModule: "scheduling",
        requiresRole: [
          "OWNER",
          "ADMIN",
          "ACCOUNTANT",
          "STAFF",
          "MENTOR",
          "PROGRAM_DIRECTOR",
          "VOLUNTEER",
        ],
      },
      {
        to: "/events",
        label: "Events",
        icon: Megaphone,
        requiresModule: "scheduling",
        requiresRole: [
          "OWNER",
          "ADMIN",
          "ACCOUNTANT",
          "STAFF",
          "MENTOR",
          "PROGRAM_DIRECTOR",
          "VOLUNTEER",
        ],
      },
      {
        to: "/programs",
        label: "Departments",
        icon: Layers,
        vocabKey: "programs",
        requiresModule: "programs",
        requiresRole: ["OWNER", "ADMIN", "STAFF", "MENTOR", "PROGRAM_DIRECTOR", "VOLUNTEER"],
      },
      {
        to: "/facilities",
        label: "Facilities",
        icon: DoorOpen,
        permissionMatrixModule: "facilities",
        requiresModule: "scheduling",
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    items: [
      {
        to: "/submission-center",
        label: "Submission Center",
        icon: Send,
        permissionMatrixModule: "submission_center",
        visibleWithAnyCrud: true,
        requiresModule: "accounting",
      },
      {
        to: "/accounting",
        label: "Accounting",
        icon: Calculator,
        vocabKey: "accounting",
        requiresModule: "accounting",
        requiresRole: ["OWNER", "ADMIN", "ACCOUNTANT", "BOARD"],
        nestedAccounting: true,
      },
      {
        to: "/donors",
        label: "Donors",
        icon: Heart,
        vocabKey: "donors",
        requiresModule: "people",
        permissionMatrixModule: "donors",
        requiresRole: ["OWNER", "ADMIN", "STAFF", "MENTOR", "PROGRAM_DIRECTOR"],
      },
      {
        to: "/grants",
        label: "Grants",
        icon: FileText,
        vocabKey: "grants",
        // Grant pipeline needs grant_management; Grant management needs accounting — gated in navVisibility.
        requiresRole: ["OWNER", "ADMIN", "ACCOUNTANT", "BOARD"],
        nestedGrants: true,
      },
      {
        to: "/vendors",
        label: "Vendors",
        icon: Truck,
        permissionMatrixModule: "vendors",
        requiresModule: "accounting",
        requiresRole: ["OWNER", "ADMIN", "ACCOUNTANT", "STAFF", "MENTOR", "PROGRAM_DIRECTOR"],
      },
    ],
  },
  {
    id: "management",
    label: "Management",
    items: [
      {
        to: "/team/invitations",
        label: "Invitation",
        icon: UserPlus,
        permissionMatrixModule: "invitations",
      },
      // {
      //   to: "/copilot/playbooks",
      //   label: "Copilot Workflows",
      //   icon: Workflow,
      //   vocabKey: "copilot",
      //   requiresModule: "ai_copilot",
      // },
      { to: "/settings", label: "Settings", icon: Settings, settingsHub: true },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    items: [
      {
        to: "/control",
        label: "Pilot Cohort",
        icon: ShieldCheck,
        badgeKey: "applications",
        requiresControl: true,
      },
      { to: "/control/kpis", label: "Cohort KPIs", icon: BarChart3, requiresControl: true },
      {
        to: "/control/analytics",
        label: "Pilot Analytics",
        icon: BarChart3,
        requiresControl: true,
      },
      {
        to: "/control/announcements",
        label: "Announcements",
        icon: Megaphone,
        requiresControl: true,
      },
      { to: "/control/billing", label: "Pilot Billing", icon: Wallet, requiresControl: true },
    ],
  },
];

const ALL_NAV_ITEMS: NavItem[] = [
  ...UNGROUPED_ITEMS,
  ...NAV_GROUPS.flatMap((group) => group.items),
];

const PARENT_PATHS: ReadonlySet<string> = new Set(
  ALL_NAV_ITEMS.filter((item) =>
    ALL_NAV_ITEMS.some((other) => other.to !== item.to && other.to.startsWith(item.to + "/")),
  ).map((item) => item.to),
);

export function isParentRoute(to: string): boolean {
  return to === "/" || PARENT_PATHS.has(to);
}

const GROUP_PATH_PREFIXES: { prefix: string; groupId: NavGroupId }[] = [
  { prefix: "/cases", groupId: "services" },
  { prefix: "/tasks", groupId: "services" },
  { prefix: "/community", groupId: "services" },
  { prefix: "/people", groupId: "services" },
  { prefix: "/check-in", groupId: "services" },
  { prefix: "/inventory", groupId: "services" },
  { prefix: "/assistance-programs", groupId: "services" },
  { prefix: "/time-tracker", groupId: "services" },
  { prefix: "/resource-directory", groupId: "services" },
  { prefix: "/scheduling", groupId: "scheduling" },
  { prefix: "/facilities", groupId: "scheduling" },
  { prefix: "/events", groupId: "scheduling" },
  { prefix: "/programs", groupId: "scheduling" },
  { prefix: "/submission-center", groupId: "finance" },
  { prefix: "/accounting", groupId: "finance" },
  { prefix: "/donors", groupId: "finance" },
  { prefix: "/grants", groupId: "finance" },
  { prefix: "/vendors", groupId: "finance" },
  { prefix: "/team", groupId: "management" },
  { prefix: "/copilot", groupId: "management" },
  { prefix: "/settings", groupId: "management" },
  { prefix: "/control", groupId: "platform" },
];

export function groupIdForPath(pathname: string): NavGroupId | null {
  for (const { prefix, groupId } of GROUP_PATH_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return groupId;
    }
  }
  return null;
}
